/**
 * MaintenanceContext - Global maintenance mode state
 * When enabled, non-admin users can only access login and dashboard
 * Reads the API's authoritative state, including while the page stays open.
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../api/client';
import useLiveNoticeRefresh from '../hooks/useLiveNoticeRefresh';
import { noticeReadOptions } from '../services/systemNoticeEvents';

const MaintenanceContext = createContext({
  maintenanceMode: false,
  maintenanceMessage: '',
  setMaintenanceMode: () => {},
  checkMaintenanceStatus: () => {},
});

export function MaintenanceProvider({ children }) {
  const [maintenanceMode, setMaintenanceModeState] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [loading, setLoading] = useState(true);

  const readMaintenanceStatus = useCallback(async (signal) => {
    try {
      const { data } = await api.get('/status/maintenance', noticeReadOptions(signal));
      if (!signal.aborted && typeof data?.enabled === 'boolean') {
        setMaintenanceModeState(data.enabled);
        setMaintenanceMessage(data.message || '');
      }
    } finally { if (!signal.aborted) setLoading(false); }
  }, []);
  const checkMaintenanceStatus = useLiveNoticeRefresh(readMaintenanceStatus);

  // Toggle maintenance mode - saves to server which writes the JSON file
  const setMaintenanceMode = useCallback(async (enabled, message = '') => {
    try {
      // Try to save to server API which will write the JSON file
      await api.post('/status/maintenance', { enabled, message });
      await checkMaintenanceStatus();
    } catch (e) {
      console.error('Failed to set maintenance mode via API:', e);
      // Show error to admin
      throw e;
    }
  }, [checkMaintenanceStatus]);

  return (
    <MaintenanceContext.Provider value={{ 
      maintenanceMode, 
      maintenanceMessage,
      setMaintenanceMode, 
      checkMaintenanceStatus,
      loading,
    }}>
      {children}
    </MaintenanceContext.Provider>
  );
}

export function useMaintenance() {
  return useContext(MaintenanceContext);
}

export default MaintenanceContext;
